import { Component, EventEmitter, Input, OnInit, Output, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { DialogService } from '@services/dialog.service';
import { ErrorService } from '@services/error.service';
import { LegacyNameService } from '@services/legacy-name.service';
import { LegacyPasswordService } from '@services/legacy-password.service';
import { PartyService } from '@services/party.service';
import { UserService } from '@services/user.service';

import { Customer } from '@models/customer';
import { Party } from '@models/party';
import { Supplier } from '@models/supplier';
import { User } from '@models/user';
import { FORM_COMP_MODES } from '@services/constants.service';
import { SsoService } from '@services/sso.service';


@Component({
  selector: 'app-employee',
  templateUrl: './employee.component.html',
  styleUrls: ['./employee.component.scss'],
  standalone: false
})
export class EmployeeComponent implements OnInit {
  @Input() party!: Customer | Supplier | null;
  @Input() username!: string;
  @Input() isAdmin: boolean = false;

  @Output() loadingEvent = new EventEmitter<boolean>();
  @Output() reloadEvent = new EventEmitter<void>();

  public user: User | null = null;
  public userParty: Party | null = null;
  public employeeForm: FormGroup;
  public defaultFormValues: any = {};
  public loadingEmployee: boolean = true;
  public relationships: any[] = [];
  public unlinkingSso: boolean = false;

  private emailChangeModal: NgbModalRef | null = null;

  // necessary to reference it in the html template
  public readonly FORM_COMP_MODES = FORM_COMP_MODES;

  constructor(
    private formBuilder: FormBuilder,
    private viewContainerRef: ViewContainerRef,
    private modalService: NgbModal,
    private translateService: TranslateService,
    private userService: UserService,
    private dialogService: DialogService,
    private errorService: ErrorService,
    private partyService: PartyService,
    private legacyPasswordService: LegacyPasswordService,
    private legacyNameService: LegacyNameService,
    private ssoService: SsoService,
  ) {
    this.employeeForm = this.formBuilder.group({
      firstname: [{
        value: "",
        disabled: true, // disabled, because otherwise the input field appears
      },
      Validators.compose([Validators.required])
      ],
      lastname: [{
        value: "",
        disabled: true,
      },
      Validators.compose([Validators.required])
      ],
      editing: [false],
    });
  }

  ngOnInit(): void {
    this.loadEmployee(this.username);
  }

  private initForm() {
    this.employeeForm.controls['editing'].patchValue(false);
    this.employeeForm.controls['firstname'].disable();
    this.employeeForm.controls['lastname'].disable();
  }

  private loadEmployee(username: string | null): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!username) {
        return reject();
      }

      return this.userService.getUser(username).then((user: User) => {
        this.user = user;
        this.employeeForm.controls['firstname'].setValue(this.user?.firstname);
        this.employeeForm.controls['lastname'].setValue(this.user?.lastname);

        return this.partyService.getParty(user.partyId!, true);
      }).then((party: Party | null) => {
        this.userParty = party;

        return resolve();
      }).catch(() => {
        return reject();
      });
    });
  }

  public async toggleUser(user: User, enable: boolean) {
    if (! await this.dialogService.confirm(
      `${enable ? this.translateService.instant('Dialog.EmployeesComponent.text_enableUser') : this.translateService.instant('Dialog.EmployeesComponent.text_disableUser')}`,
      `${enable ? this.translateService.instant('Dialog.EmployeesComponent.text_confirmEnableUser') : this.translateService.instant('Dialog.EmployeesComponent.text_confirmDisableUser')}`,
      this.viewContainerRef
    )) {
      return;
    }

    this.loadingEvent.emit(true);

    this.userService.updateEnabled(enable, user?.username).then(() => {
      this.dialogService.alert(null, `${enable ? this.translateService.instant('Dialog.EmployeesComponent.text_UserEnabled') : this.translateService.instant('Dialog.EmployeesComponent.text_UserDisabled')}`, 'success');
      this.reload();
      this.loadingEvent.emit(false);
    }).catch((error: string) => {
      this.dialogService.alert(null, error || `${enable ? this.translateService.instant('Dialog.EmployeesComponent.text_errEnablingUser') : this.translateService.instant('Dialog.EmployeesComponent.text_errDisablingUser')}`, 'danger');
      this.loadingEvent.emit(false);
    });
  }

  public async unlinkUser(user: User) {
    if (! await this.dialogService.confirm(
      this.translateService.instant('Dialog.EmployeesComponent.text_UnlinkUser'),
      this.translateService.instant('Dialog.EmployeesComponent.text_conformUnlinkUser'),
      this.viewContainerRef
    )) {
      return;
    }

    if (!this.party || !this.party.partyId || !user || !user.partyId) {
      this.errorService.printError("Either party or user are empty.");
      return;
    }

    this.loadingEvent.emit(true);

    this.userService.unlinkUser(this.party.partyId, user.partyId).then(() => {
      this.dialogService.alert(null, this.translateService.instant('Dialog.EmployeesComponent.text_userUnlinked'), 'success');
      this.reload();
      this.loadingEvent.emit(false);
      this.reloadEvent.emit();
    }).catch((error: string) => {
      this.dialogService.alert(null, error || this.translateService.instant('Dialog.EmployeesComponent.text_errUserNotUnlinked'), 'danger');
      this.loadingEvent.emit(false);
    })
  }

  public async deleteUser(user: User) {
    if (! await this.dialogService.confirm(
      this.translateService.instant('Dialog.EmployeesComponent.text_removeUser'),
      this.translateService.instant('Dialog.EmployeesComponent.text_confirmRemoveUser'),
      this.viewContainerRef
    )) {
      return;
    }

    this.loadingEvent.emit(true);

    this.userService.deleteUser(user!.username).then(() => {
      this.dialogService.alert(null, this.translateService.instant('Dialog.EmployeesComponent.text_userRemoved'), 'success');
      this.reload();
      // this.dialogService.dismissLoader(`${this.LOADER_NAME}.deleteUser`);
      this.loadingEvent.emit(false);
      this.reloadEvent.emit();
    }).catch((error: string) => {
      this.dialogService.alert(null, error || this.translateService.instant('Dialog.EmployeesComponent.text_userNotRemoved'), 'danger');
      this.loadingEvent.emit(false);
    })
  }

  public async resetPassword(user: User) {
    if (! await this.dialogService.confirm(
      this.translateService.instant('Dialog.EmployeesComponent.text_resetPassword'),
      this.translateService.instant('Dialog.EmployeesComponent.text_confirmResetPassword'),
      this.viewContainerRef
    )) {
      return;
    }

    this.loadingEvent.emit(true);

    this.userService.getExternalAuthId(user.email).then((externalAuthId: string | null) => {
      if (externalAuthId) {
        // return this.ssoPasswordService.resetPassword();
        this.dialogService.alert(null, "Password reset has to be done by the user himself.", 'danger');
        return Promise.reject("Password reset has to be done by the user himself.");
      } else {
        return this.legacyPasswordService.resetPassword(user.username);
      }
    }).then(() => {
      this.dialogService.alert(null, this.translateService.instant('Dialog.EmployeesComponent.text_passwordReset'), 'success');
      this.reload();
      this.loadingEvent.emit(false);
    }).catch((error: string) => {
      this.dialogService.alert(null, error || this.translateService.instant('Dialog.EmployeesComponent.text_errPasswordNotReset'), 'danger');
      this.loadingEvent.emit(false);
    });
  }

  public setUserAttribute(user: User, attributeTypeId: string, attributeValue: string) {
    if (!user || !user.partyId) {
      return;
    }
    this.loadingEvent.emit(true);

    this.partyService.setAttribute(user.partyId, attributeTypeId, attributeValue).then(() => {
      this.dialogService.alert(null, this.translateService.instant('Dialog.EmployeesComponent.text_attributeUpdated'), 'success');

      return this.reload();
    }).then(() => {
      this.loadingEvent.emit(false);
    }).catch((error: string) => {
      this.dialogService.alert(null, error || this.translateService.instant('Dialog.EmployeesComponent.text_errAttributeNotUpdated'), 'danger');
      this.loadingEvent.emit(false);
    });
  }

  public updateUserName(user: User, firstname: string, lastname: string, formGroup: FormGroup<any>) {
    user.loading = true;
    return this.legacyNameService.updateName(firstname, lastname, user.username).then(() => {
      user.loading = false;
      this.reload();
    }).catch((err) => {
      user.loading = false;
      this.errorService.handleError(err);
      this.dialogService.alert(null, err || this.translateService.instant('Dialog.EmployeesComponent.text_errNameNotUpdated'), 'danger');
    })
  }

  public reload(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.party != null) {
        this.defaultFormValues = this.party;
      }
      return this.loadEmployee(this.username).then(() => {
        this.initForm();

        return resolve();
      }).catch(() => {
        return reject();
      });
    });
  }

  public showForm(form: any, defaultValues: any, editingControl: any) {
    if (editingControl) {
      if (editingControl.value) {
        editingControl.patchValue(false);

        Object.keys(form.controls).forEach((fieldName: string) => {
          form.controls[fieldName].patchValue(this.defaultFormValues[fieldName] || defaultValues[fieldName] || '');
          form.controls[fieldName].disable();
        });
      } else {
        editingControl.patchValue(true);

        Object.keys(form.controls).forEach((fieldName: string) => {
          form.controls[fieldName].enable();
        });
      }
    }
  }

  public openModal(modal: any) {
    this.emailChangeModal = this.modalService.open(modal);
    this.emailChangeModal.result.then(() => {
      this.reload();
    }, () => {
      this.reload();
    });
  }

  public exitEmailForm(event: boolean) {
    if (event == true) {
      this.dialogService.alert(
        null,
        this.translateService.instant("Email erfolgreich geändert."),
        "success"
      );
    } else {
      this.dialogService.alert(
        null,
        this.translateService.instant("Email konnte nicht geändert werden."),
        "danger"
      );
    }
  }

  public unlinkSsoAccount() {
    this.unlinkingSso = true;

    this.ssoService.unlinkAccount(this.user!.username).then(() => {
      this.dialogService.alert(null, 'Die Verbindung zum SSO-Account wurde getrennt. Der Benutzer kann sich nun wieder mit seinen alten MedienCenter-Zugangsdaten anmelden.', 'success');
      this.reload();
      this.unlinkingSso = false;
    }).catch((error: string) => {
      this.dialogService.alert(null, error || 'Fehler beim Trennen des SSO-Accounts.', 'danger');
      this.unlinkingSso = false;
    });
  }

  public showSsoPanel(user: User): void {
    this.ssoService.showSsoPanel(user);
  }

}
